/**
 * Job Briefing Stage Component
 * First stage where user analyzes the job requirements
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
    ArrowRight,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Loader2,
    Target,
    FileText,
    Star,
    ChevronRight,
    ThumbsUp,
    ThumbsDown,
    Eye
} from 'lucide-react';

const JobBriefingStage = ({ jobBrief, onContinue, loading }) => {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-500" />
                    Job Brief Analysis
                </CardTitle>
                <CardDescription>
                    Study this job brief carefully. Understanding the requirements is key to effective sourcing.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Job Title & Company */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5 border">
                    <h3 className="text-xl font-bold">{jobBrief?.title}</h3>
                    <p className="text-muted-foreground">
                        {jobBrief?.company_type} • {jobBrief?.industry} • {jobBrief?.department}
                    </p>
                </div>

                {/* Key Details Grid */}
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-3 rounded-lg bg-muted/30">
                        <p className="text-xs text-muted-foreground mb-1">Experience Required</p>
                        <p className="font-medium">{jobBrief?.experience_range?.min}-{jobBrief?.experience_range?.max} years</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                        <p className="text-xs text-muted-foreground mb-1">Location</p>
                        <p className="font-medium">{jobBrief?.location}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                        <p className="text-xs text-muted-foreground mb-1">Work Mode</p>
                        <p className="font-medium capitalize">{jobBrief?.work_mode}</p>
                    </div>
                </div>

                {/* Required Skills */}
                <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Star className="h-4 w-4 text-amber-500" />
                        Required Skills
                    </h4>
                    <div className="flex flex-wrap gap-2">
                        {jobBrief?.required_skills?.map((skill, idx) => (
                            <Badge key={idx} variant="default">{skill}</Badge>
                        ))}
                    </div>
                </div>

                {/* Preferred Skills */}
                {jobBrief?.preferred_skills?.length > 0 && (
                    <div>
                        <h4 className="font-semibold mb-2 text-muted-foreground">Preferred Skills</h4>
                        <div className="flex flex-wrap gap-2">
                            {jobBrief?.preferred_skills?.map((skill, idx) => (
                                <Badge key={idx} variant="outline">{skill}</Badge>
                            ))}
                        </div>
                    </div>
                )}

                {/* Key Responsibilities */}
                <div>
                    <h4 className="font-semibold mb-2">Key Responsibilities</h4>
                    <ul className="space-y-1 text-sm">
                        {jobBrief?.key_responsibilities?.map((resp, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                                <ChevronRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                                {resp}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Ideal Background - IMPORTANT */}
                <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/30">
                    <h4 className="font-semibold mb-2 flex items-center gap-2 text-emerald-600">
                        <CheckCircle className="h-4 w-4" />
                        Ideal Background (Good Sources)
                    </h4>
                    <ul className="space-y-1 text-sm">
                        {jobBrief?.ideal_background?.map((bg, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                                <ThumbsUp className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                {bg}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Red Flag Backgrounds - IMPORTANT */}
                <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/30">
                    <h4 className="font-semibold mb-2 flex items-center gap-2 text-red-600">
                        <XCircle className="h-4 w-4" />
                        Red Flag Backgrounds (Poor Sources)
                    </h4>
                    <ul className="space-y-1 text-sm">
                        {jobBrief?.red_flag_backgrounds?.map((bg, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                                <ThumbsDown className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                                {bg}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Deal Breakers */}
                <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/30">
                    <h4 className="font-semibold mb-2 flex items-center gap-2 text-amber-600">
                        <AlertTriangle className="h-4 w-4" />
                        Deal Breakers
                    </h4>
                    <ul className="space-y-1 text-sm">
                        {jobBrief?.deal_breakers?.map((db, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                                <XCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                                {db}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Critical Evaluation Points */}
                <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Target className="h-4 w-4 text-primary" />
                        Critical Evaluation Points
                    </h4>
                    <ul className="space-y-1 text-sm">
                        {jobBrief?.critical_evaluation_points?.map((point, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                                <Eye className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                                {point}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Continue Button */}
                <div className="pt-4 border-t">
                    <Button 
                        onClick={onContinue} 
                        disabled={loading}
                        className="w-full"
                        data-testid="continue-to-search"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <>
                                I&apos;ve Analyzed the Job Brief
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </>
                        )}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default JobBriefingStage;
